import {
  faArrowRight,
  faBan,
  faBook,
  faBuilding,
  faCheck,
  faChevronDown,
  faCircle,
  faClock,
  faCommentDots,
  faEllipsisH,
  faEnvelope,
  faFilter,
  faGlobeAmericas,
  faHeadset,
  faLink,
  faMapMarkerAlt,
  faMicrophone,
  faPalette,
  faPaperPlane,
  faPaperclip,
  faPhone,
  faPlus,
  faSearch,
  faSmile,
  faTrashAlt,
  faUser,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import type { EmojiData } from "emoji-mart";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button } from "reactstrap";
import SupportChatLivePreview from "../component/support/SupportChatLivePreview";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../styles/WembiiAdminSupport.scss";
import { SupportConversation, SupportVisitor } from "../../cores/wembiiSupport";
import {
  defaultSupportAppearance,
  useWembiiAdminSupportScreen,
} from "./hooks/useWembiiAdminSupportScreen";

type SupportTab = "visitors" | "conversations" | "appearance";

interface SupportChatAppearance {
  widgetTitle: string;
  welcomeTitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  ctaLabel: string;
  onlineLabel: string;
  headerColor: string;
  bubbleColor: string;
  widgetBg: string;
  textColor: string;
  accentColor: string;
  position: "left" | "right";
  avatarType: "logo" | "agent";
  showAgentName: boolean;
  showTimestamp: boolean;
  roundedStyle: "soft" | "rounded" | "square";
  showQuickButtons: boolean;
  showCommercialQuickButton: boolean;
  showTechnicalQuickButton: boolean;
  quickButtonCommercial: string;
  quickButtonTechnical: string;
}

const defaultAppearance = defaultSupportAppearance;

const TILE_SIZE = 256;
const SOUTH_AMERICA_CENTER = { lat: -20.5, lng: -62.5 };
const MIN_MAP_ZOOM = 3;
const MAX_MAP_ZOOM = 16;
const VISITOR_FOCUS_ZOOM = 12;

function getVisitorLocationKey(visitor: SupportVisitor) {
  return `${visitor.city}, ${visitor.country}`;
}

function getVisitorMapKey(visitor: SupportVisitor) {
  if (
    visitor.lat != null &&
    visitor.lng != null &&
    !Number.isNaN(visitor.lat) &&
    !Number.isNaN(visitor.lng)
  ) {
    return `${Number(visitor.lat).toFixed(3)},${Number(visitor.lng).toFixed(3)}`;
  }
  return getVisitorLocationKey(visitor);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function formatLocationLabel(
  city?: string,
  country?: string,
  session?: { lat?: number | null; lng?: number | null } | null
) {
  const normalizedCity = city?.trim();
  const normalizedCountry = country?.trim();
  if (normalizedCity && normalizedCountry) return `${normalizedCity}, ${normalizedCountry}`;
  if (normalizedCity) return normalizedCity;
  if (normalizedCountry) return normalizedCountry;
  if (session?.lat != null && session?.lng != null) {
    return `~${Number(session.lat).toFixed(2)}, ${Number(session.lng).toFixed(2)}`;
  }
  return "Ubicación no disponible";
}

function formatIpLabel(ip?: string) {
  if (!ip) return "IP no disponible";
  if (ip === "::1" || ip === "127.0.0.1") return "Localhost (desarrollo)";
  return ip;
}

function latLngToWorldPixel(lat: number, lng: number, zoom: number) {
  const sinLat = Math.sin((clamp(lat, -85.05112878, 85.05112878) * Math.PI) / 180);
  const scale = TILE_SIZE * Math.pow(2, zoom);
  return {
    x: ((lng + 180) / 360) * scale,
    y:
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
      scale,
  };
}

function worldPixelToLatLng(x: number, y: number, zoom: number) {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const lng = (x / scale) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale)));
  const lat = (latRad * 180) / Math.PI;
  return { lat, lng };
}

function mapPointToViewport(
  lat: number,
  lng: number,
  zoom: number,
  size: { width: number; height: number },
  centerLatLng: { lat: number; lng: number }
) {
  const center = latLngToWorldPixel(centerLatLng.lat, centerLatLng.lng, zoom);
  const point = latLngToWorldPixel(lat, lng, zoom);
  return {
    left: point.x - center.x + size.width / 2,
    top: point.y - center.y + size.height / 2,
  };
}


function getSupportInitials(name?: string) {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

const countryNameToCode: Record<string, string> = {
  Argentina: "AR",
  Chile: "CL",
  Uruguay: "UY",
  Brasil: "BR",
  Brazil: "BR",
  Colombia: "CO",
  Perú: "PE",
  Peru: "PE",
  México: "MX",
  Mexico: "MX",
  España: "ES",
  Spain: "ES",
};

function normalizeCountryCode(code?: string, country?: string) {
  const upper = String(code || "").trim().toUpperCase();
  if (upper.length === 2) return upper;
  return countryNameToCode[country || ""] || "";
}

function getCountryFlagUrl(countryCode?: string, country?: string) {
  const normalized = normalizeCountryCode(countryCode, country).toLowerCase();
  if (!normalized) return null;
  return `https://flagcdn.com/w40/${normalized}.png`;
}

type AvatarPresence = "online" | "idle" | "offline";

function resolveAvatarPresence(
  presence?: AvatarPresence,
  status?: SupportVisitor["status"]
): AvatarPresence | undefined {
  if (presence) return presence;
  if (status === "online") return "online";
  if (status === "idle") return "idle";
  return undefined;
}

function getPresenceTitle(presence: AvatarPresence) {
  if (presence === "online") return "En línea";
  if (presence === "idle") return "Inactivo";
  return "Desconectado";
}

function getConversationPresence(
  conversation: SupportConversation,
  visitors: SupportVisitor[]
): AvatarPresence {
  const liveVisitor = visitors.find(
    (visitor) =>
      visitor.conversationId === conversation.id ||
      visitor.id === conversation.visitorId ||
      visitor.userId === conversation.visitorId
  );
  if (liveVisitor) {
    return liveVisitor.status === "online" ? "online" : "idle";
  }
  return conversation.isOnline || conversation.session?.isOnline
    ? "online"
    : "offline";
}

function SupportAvatar(props: {
  className: string;
  color: string;
  name?: string;
  avatarUrl?: string | null;
  flag?: string;
  countryCode?: string;
  country?: string;
  status?: SupportVisitor["status"];
  presence?: AvatarPresence;
  showPresence?: boolean;
}) {
  const {
    className,
    color,
    name,
    avatarUrl,
    flag,
    countryCode,
    country,
    status,
    presence,
    showPresence = true,
  } = props;
  const [imageFailed, setImageFailed] = useState(false);
  const [flagImageFailed, setFlagImageFailed] = useState(false);
  const normalizedAvatarUrl = avatarUrl?.trim() || undefined;
  const resolvedPresence = resolveAvatarPresence(presence, status);
  const normalizedCountryCode = normalizeCountryCode(countryCode, country);
  const flagUrl = getCountryFlagUrl(countryCode, country);
  const showFlagBadge = Boolean(flagUrl || normalizedCountryCode || flag);

  useEffect(() => {
    setImageFailed(false);
  }, [normalizedAvatarUrl]);

  useEffect(() => {
    setFlagImageFailed(false);
  }, [flagUrl]);

  const showImage = Boolean(normalizedAvatarUrl && !imageFailed);
  const initials = getSupportInitials(name);

  return (
    <span className={className} style={{ background: showImage ? "#fff" : color }}>
      {showImage ? (
        <img
          src={normalizedAvatarUrl}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="support-avatar__initials" aria-hidden="true">
          {initials}
        </span>
      )}
      {showPresence && resolvedPresence && (
        <span
          className="support-avatar__presence"
          data-presence={resolvedPresence}
          title={getPresenceTitle(resolvedPresence)}
        />
      )}
      {showFlagBadge ? (
        <span className="support-avatar__flag" aria-hidden="true">
          {flagUrl && !flagImageFailed ? (
            <img
              src={flagUrl}
              alt=""
              loading="lazy"
              onError={() => setFlagImageFailed(true)}
            />
          ) : normalizedCountryCode ? (
            <span className="support-avatar__flag-code">{normalizedCountryCode}</span>
          ) : (
            flag
          )}
        </span>
      ) : null}
    </span>
  );
}

export default function WembiiAdminSupport() {
  const [activeTab, setActiveTab] = useState<SupportTab>("visitors");
  const {
    appearance,
    conversations,
    selectedConversation,
    messages,
    messagesLoading,
    supportVisitors,
    replyDraft,
    setReplyDraft,
    sendAgentReply,
    setAppearanceField,
    resetAppearance,
    uploadAvatar,
    openVisitorConversation: openVisitorFromHook,
    selectConversation,
    formatLocalTime,
  } = useWembiiAdminSupportScreen(activeTab);

  const mapRef = useRef<HTMLElement>(null);
  const mapCenterRef = useRef(SOUTH_AMERICA_CENTER);
  const mapZoomRef = useRef(4);
  const mapDragRef = useRef<{
    startX: number;
    startY: number;
    center: { lat: number; lng: number };
    zoom: number;
  } | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const emojiToggleRef = useRef<HTMLButtonElement>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const [mapZoom, setMapZoom] = useState(4);
  const [mapCenter, setMapCenter] = useState(SOUTH_AMERICA_CENTER);
  const [mapSize, setMapSize] = useState({ width: 900, height: 620 });
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedVisitorId, setSelectedVisitorId] = useState<string | null>(null);
  const [isMapDragging, setIsMapDragging] = useState(false);

  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  useEffect(() => {
    mapZoomRef.current = mapZoom;
  }, [mapZoom]);

  useEffect(() => {
    if (!emojiOpen) return;
    const closeIfOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        emojiPanelRef.current?.contains(target) ||
        emojiToggleRef.current?.contains(target)
      ) {
        return;
      }
      setEmojiOpen(false);
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [emojiOpen]);

  const onEmojiSelect = useCallback(
    (emoji: EmojiData) => {
      if ("native" in emoji && emoji.native) {
        setReplyDraft((prev) => prev + emoji.native);
      }
      replyInputRef.current?.focus();
    },
    [setReplyDraft]
  );

  const zoomMapAtPoint = useCallback(
    (clientX: number, clientY: number, delta: number) => {
      const mapEl = mapRef.current;
      if (!mapEl) return;

      const rect = mapEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const currentZoom = mapZoomRef.current;
      const nextZoom = clamp(currentZoom + delta, MIN_MAP_ZOOM, MAX_MAP_ZOOM);
      if (nextZoom === currentZoom) return;

      const center = mapCenterRef.current;
      const centerPx = latLngToWorldPixel(center.lat, center.lng, currentZoom);
      const focusPx = {
        x: centerPx.x + (x - mapSize.width / 2),
        y: centerPx.y + (y - mapSize.height / 2),
      };
      const focusLatLng = worldPixelToLatLng(focusPx.x, focusPx.y, currentZoom);
      const focusPxNext = latLngToWorldPixel(
        focusLatLng.lat,
        focusLatLng.lng,
        nextZoom
      );
      const nextCenterPx = {
        x: focusPxNext.x - (x - mapSize.width / 2),
        y: focusPxNext.y - (y - mapSize.height / 2),
      };

      setMapZoom(nextZoom);
      setMapCenter(worldPixelToLatLng(nextCenterPx.x, nextCenterPx.y, nextZoom));
    },
    [mapSize.height, mapSize.width]
  );

  const handleMapMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      mapDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        center: { ...mapCenterRef.current },
        zoom: mapZoomRef.current,
      };
      setIsMapDragging(true);
    },
    []
  );

  useEffect(() => {
    if (!isMapDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const drag = mapDragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const centerPx = latLngToWorldPixel(drag.center.lat, drag.center.lng, drag.zoom);
      const nextCenter = worldPixelToLatLng(
        centerPx.x - dx,
        centerPx.y - dy,
        drag.zoom
      );
      setMapCenter(nextCenter);
    };

    const handleMouseUp = () => {
      mapDragRef.current = null;
      setIsMapDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isMapDragging]);

  useEffect(() => {
    const mapEl = mapRef.current;
    if (!mapEl || activeTab !== "visitors") return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -1 : 1;
      zoomMapAtPoint(event.clientX, event.clientY, delta);
    };

    mapEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => mapEl.removeEventListener("wheel", handleWheel);
  }, [activeTab, zoomMapAtPoint]);

  const openVisitorConversation = async (visitor: SupportVisitor) => {
    const opened = await openVisitorFromHook(visitor);
    if (!opened) return;
    setActiveTab("conversations");
    setFilterOpen(false);
    setTopMenuOpen(false);
    setKnowledgeOpen(false);
  };

  const focusVisitorOnMap = useCallback((visitor: SupportVisitor) => {
    if (visitor.lat == null || visitor.lng == null) return;
    setSelectedVisitorId(visitor.id);
    setSelectedLocationId(getVisitorMapKey(visitor));
    setMapCenter({ lat: visitor.lat, lng: visitor.lng });
    setMapZoom(VISITOR_FOCUS_ZOOM);
  }, []);

  const focusLocationOnMap = useCallback(
    (location: { id: string; lat: number; lng: number }) => {
      setSelectedLocationId(location.id);
      setMapCenter({ lat: location.lat, lng: location.lng });
      setMapZoom((zoom) => clamp(Math.max(zoom, VISITOR_FOCUS_ZOOM - 1), MIN_MAP_ZOOM, MAX_MAP_ZOOM));
    },
    []
  );

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const syncSize = () =>
      setMapSize({ width: el.clientWidth || 900, height: el.clientHeight || 620 });
    syncSize();
    const observer = new ResizeObserver(syncSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab]);

  useEffect(() => {
    document.body.classList.add("hide-wembii-support-floating-widget");
    return () =>
      document.body.classList.remove("hide-wembii-support-floating-widget");
  }, []);

  useEffect(() => {
    if (activeTab !== "conversations" || messagesLoading) return;
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeTab, messages, messagesLoading, selectedConversation?.id]);

  const onlineVisitors = supportVisitors.filter(
    (visitor) => visitor.status === "online"
  );

  const mapLocations = useMemo(() => {
    const mappableVisitors = supportVisitors.filter(
      (visitor) =>
        visitor.id !== "detected-browser-user" &&
        visitor.lat != null &&
        visitor.lng != null &&
        !Number.isNaN(visitor.lat) &&
        !Number.isNaN(visitor.lng)
    );

    const grouped = mappableVisitors.reduce<Record<string, SupportVisitor[]>>(
      (acc, visitor) => {
        const key = `${Number(visitor.lat).toFixed(3)},${Number(visitor.lng).toFixed(3)}`;
        acc[key] = [...(acc[key] ?? []), visitor];
        return acc;
      },
      {}
    );

    return Object.entries(grouped).map(([id, group]) => ({
      id,
      label: getVisitorLocationKey(group[0]),
      flag: group[0].flag,
      visitors: group,
      lat: group.reduce((sum, visitor) => sum + (visitor.lat ?? 0), 0) / group.length,
      lng: group.reduce((sum, visitor) => sum + (visitor.lng ?? 0), 0) / group.length,
    }));
  }, [supportVisitors]);

  const selectedLocation = selectedLocationId
    ? mapLocations.find((location) => location.id === selectedLocationId) ?? null
    : null;

  const selectedVisitor = selectedVisitorId
    ? supportVisitors.find((visitor) => visitor.id === selectedVisitorId) ?? null
    : null;

  const mapTiles = useMemo(() => {
    const center = latLngToWorldPixel(mapCenter.lat, mapCenter.lng, mapZoom);
    const worldTiles = Math.pow(2, mapZoom);
    const cols = Math.ceil(mapSize.width / TILE_SIZE) + 3;
    const rows = Math.ceil(mapSize.height / TILE_SIZE) + 3;
    const startX = Math.floor((center.x - mapSize.width / 2) / TILE_SIZE) - 1;
    const startY = Math.floor((center.y - mapSize.height / 2) / TILE_SIZE) - 1;
    const tiles: Array<{
      id: string;
      x: number;
      y: number;
      srcX: number;
      srcY: number;
      left: number;
      top: number;
    }> = [];

    for (let dx = 0; dx < cols; dx += 1) {
      for (let dy = 0; dy < rows; dy += 1) {
        const x = startX + dx;
        const y = clamp(startY + dy, 0, worldTiles - 1);
        const srcX = ((x % worldTiles) + worldTiles) % worldTiles;
        tiles.push({
          id: `${mapZoom}-${x}-${y}`,
          x,
          y,
          srcX,
          srcY: y,
          left: x * TILE_SIZE - center.x + mapSize.width / 2,
          top: y * TILE_SIZE - center.y + mapSize.height / 2,
        });
      }
    }
    return tiles;
  }, [mapCenter.lat, mapCenter.lng, mapSize.height, mapSize.width, mapZoom]);

  return (
    <Layout>
      <Content className="wembii-admin-support-content">
        <div
          className={cn("wembii-admin-support", {
            "wembii-admin-support--appearance-active": activeTab === "appearance",
          })}
        >
          <div className="wembii-admin-support__hero">
            <div>
              <div className="wembii-admin-support__badge">
                <FontAwesomeIcon icon={faHeadset} />
                Soporte Wembii
              </div>
              <h2>Centro de soporte gestionado por Wembii</h2>
            </div>
            <div className="wembii-admin-support__tabs" role="tablist">
              <button
                type="button"
                className={cn({ active: activeTab === "visitors" })}
                onClick={() => setActiveTab("visitors")}
              >
                <FontAwesomeIcon icon={faGlobeAmericas} />
                Visitantes
              </button>
              <button
                type="button"
                className={cn({ active: activeTab === "conversations" })}
                onClick={() => setActiveTab("conversations")}
              >
                <FontAwesomeIcon icon={faCommentDots} />
                Conversaciones
              </button>
              <button
                type="button"
                className={cn({ active: activeTab === "appearance" })}
                onClick={() => setActiveTab("appearance")}
              >
                <FontAwesomeIcon icon={faPalette} />
                Apariencia chat
              </button>
            </div>
          </div>

          {activeTab === "visitors" ? (
            <div className="support-live">
              <aside className="support-live__sidebar">
                <div className="support-live__head">
                  <span>Visitantes</span>
                  <Badge color="warning" pill>
                    En vivo
                  </Badge>
                </div>
                <div className="support-live__list">
                  {supportVisitors.map((visitor) => (
                    <div
                      key={visitor.id}
                      className={cn("support-live-user", {
                        "support-live-user--active": selectedVisitorId === visitor.id,
                      })}
                    >
                      <button
                        type="button"
                        className="support-live-user__main"
                        onClick={() => focusVisitorOnMap(visitor)}
                      >
                        <SupportAvatar
                          className="support-avatar support-avatar--lg"
                          color={visitor.color}
                          name={visitor.name}
                          avatarUrl={visitor.avatarUrl}
                          flag={visitor.flag}
                          countryCode={visitor.countryCode}
                          country={visitor.country}
                          status={
                            visitor.id === "detected-browser-user"
                              ? undefined
                              : visitor.status
                          }
                          showPresence={visitor.id !== "detected-browser-user"}
                        />
                        <span>
                          <strong>{visitor.name}</strong>
                          <small>{visitor.wembiiCompanyName}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="support-live-user__chat"
                        aria-label={`Ir al chat con ${visitor.name}`}
                        title="Ir al chat"
                        onClick={() => openVisitorConversation(visitor)}
                      >
                        <FontAwesomeIcon icon={faCommentDots} />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="support-live-search">
                  <FontAwesomeIcon icon={faSearch} />
                  <input placeholder="Filtrar por nombre, ciudad..." />
                </label>
              </aside>
              <section
                className={cn("support-map", {
                  "support-map--dragging": isMapDragging,
                })}
                ref={mapRef}
              >
                <div
                  className="support-map__surface"
                  onMouseDown={handleMapMouseDown}
                  role="presentation"
                >
                  <div className="support-map__tile-layer" aria-hidden>
                    {mapTiles.map((tile) => (
                      <img
                        key={tile.id}
                        className="support-map__tile"
                        alt=""
                        draggable={false}
                        src={`https://tile.openstreetmap.org/${mapZoom}/${tile.srcX}/${tile.srcY}.png`}
                        style={{
                          left: tile.left,
                          top: tile.top,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="support-map-card support-map__overlay">
                  <strong>{supportVisitors.length}</strong> usuarios en línea
                  <br />
                  <strong className="text-success">{onlineVisitors.length}</strong>{" "}
                  usuarios activos ahora
                  <small>Zoom {mapZoom} · OpenStreetMap + MagicMap</small>
                </div>
                {selectedLocation && (
                <div className="support-map-location-panel support-map__overlay">
                  <div className="support-map-location-panel__head">
                    <span>{selectedLocation.flag}</span>
                    <strong>{selectedLocation.label}</strong>
                    <button
                      type="button"
                      aria-label="Cerrar"
                      onClick={() => setSelectedLocationId("")}
                    >
                      ×
                    </button>
                    <small>
                      {selectedLocation.visitors.length === 1
                        ? "1 visitante en esta ubicación"
                        : `${selectedLocation.visitors.length} visitantes en esta ubicación`}
                    </small>
                  </div>
                  <div className="support-map-location-panel__body">
                    {selectedLocation.visitors.map((visitor) => (
                      <div
                        key={visitor.id}
                        className={cn("support-map-location-user-row", {
                          "support-map-location-user-row--active":
                            selectedVisitorId === visitor.id,
                        })}
                      >
                        <button
                          type="button"
                          className="support-map-location-user"
                          onClick={() => focusVisitorOnMap(visitor)}
                        >
                          <SupportAvatar
                            className="support-avatar support-avatar--lg"
                            color={visitor.color}
                            name={visitor.name}
                            avatarUrl={visitor.avatarUrl}
                            flag={visitor.flag}
                            countryCode={visitor.countryCode}
                            country={visitor.country}
                            status={
                              visitor.id === "detected-browser-user"
                                ? undefined
                                : visitor.status
                            }
                            showPresence={visitor.id !== "detected-browser-user"}
                          />
                          <span>
                            <strong>{visitor.name}</strong>
                            <small>{visitor.wembiiCompanyName}</small>
                          </span>
                        </button>
                        <button
                          type="button"
                          className="support-live-user__chat"
                          aria-label={`Ir al chat con ${visitor.name}`}
                          title="Ir al chat"
                          onClick={() => openVisitorConversation(visitor)}
                        >
                          <FontAwesomeIcon icon={faCommentDots} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                )}
                {mapLocations.map((location) => {
                  const position = mapPointToViewport(
                    location.lat,
                    location.lng,
                    mapZoom,
                    mapSize,
                    mapCenter
                  );
                  const isSelected = selectedLocation?.id === location.id;
                  return (
                    <button
                      key={location.id}
                      type="button"
                      className={cn("support-map-location-marker support-map__overlay", {
                        "support-map-location-marker--selected": isSelected,
                        "support-map-location-marker--focused":
                          !!selectedVisitor &&
                          location.visitors.some((visitor) => visitor.id === selectedVisitor.id),
                        "support-map-location-marker--detected":
                          location.id.includes("Browser"),
                      })}
                      style={{
                        left: position.left,
                        top: position.top,
                      }}
                      title={`${location.label} · ${location.visitors.length} usuarios`}
                      onClick={() => focusLocationOnMap(location)}
                    >
                      <span className="support-map-marker support-map-marker--cluster">
                        {location.visitors.length}
                      </span>
                      {isSelected && (
                        <span className="support-map-tooltip">
                          <strong>
                            {location.flag} {location.label}
                          </strong>
                          <span>
                            {location.visitors.length === 1
                              ? "1 visitante en línea"
                              : `${location.visitors.length} visitantes en línea`}
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
                <div className="support-map-zoom support-map__overlay">
                  <button
                    type="button"
                    onClick={() =>
                      setMapZoom((zoom) => clamp(zoom + 1, MIN_MAP_ZOOM, MAX_MAP_ZOOM))
                    }
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setMapZoom((zoom) => clamp(zoom - 1, MIN_MAP_ZOOM, MAX_MAP_ZOOM))
                    }
                  >
                    −
                  </button>
                </div>
                <div className="support-map-attribution">
                  © OpenStreetMap · detección vía navegador
                </div>
              </section>
            </div>
          ) : activeTab === "conversations" ? (
            <div className="support-inbox">
              <aside className="support-inbox__list">
                <div className="support-inbox-toolbar">
                  <div className="support-select-wrap">
                    <button
                      type="button"
                      className="support-filter-main"
                      onClick={() => setFilterOpen((open) => !open)}
                    >
                      Todos <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                    {filterOpen && (
                      <div className="support-dropdown support-dropdown--filters">
                        {[
                          ["Todos", faCircle],
                          ["No leídos", faCommentDots],
                          ["No resueltos", faArrowRight],
                          ["Menciones", faEnvelope],
                          ["Más recientes", faArrowRight],
                          ["La espera más larga", faClock],
                          ["Resueltos", faCheck],
                        ].map(([label, icon], idx) => (
                          <button key={String(label)} type="button" className={cn({ active: idx === 0 })}>
                            <FontAwesomeIcon icon={icon as typeof faCircle} />
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" className="support-toolbar-btn">
                    <FontAwesomeIcon icon={faFilter} /> Filtros
                  </button>
                  <button type="button" className="support-plus-btn">
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                <div className="support-conversation-list">
                  {conversations.map((conversation, index) => (
                    <button
                      key={conversation.id}
                      type="button"
                      className={cn("support-conversation-item", {
                        selected: conversation.id === selectedConversation?.id,
                        unread: conversation.unread,
                      })}
                      onClick={() => selectConversation(conversation.id)}
                    >
                      <SupportAvatar
                        className="support-avatar support-avatar--xl"
                        color={conversation.color}
                        name={conversation.name}
                        avatarUrl={conversation.avatarUrl}
                        flag={conversation.flag}
                        countryCode={conversation.countryCode}
                        country={conversation.country}
                        presence={getConversationPresence(conversation, supportVisitors)}
                      />
                      <span className="support-conversation-item__body">
                        <strong>{conversation.name}</strong>
                        <small>{conversation.wembiiCompanyName}</small>
                        <small>{conversation.snippet}</small>
                      </span>
                      <span className="support-conversation-item__meta">
                        <span>{conversation.date}</span>
                        {index === 0 ? (
                          <span className="support-assignee">RA</span>
                        ) : (
                          <span className="support-arrow">
                            <FontAwesomeIcon icon={faArrowRight} />
                          </span>
                        )}
                      </span>
                      {index === 0 && (
                        <span className="support-row-menu">
                          <FontAwesomeIcon icon={faEllipsisH} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </aside>

              <main className="support-chat-admin">
                <div className="support-chat-topbar">
                  <div className="support-chat-actions">
                    <button type="button">
                      <FontAwesomeIcon icon={faPhone} />
                    </button>
                    <button type="button">
                      <FontAwesomeIcon icon={faVideo} />
                    </button>
                    <button type="button">
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                    <div className="support-select-wrap">
                      <button
                        type="button"
                        className={cn("support-more-btn", { active: topMenuOpen })}
                        onClick={() => setTopMenuOpen((open) => !open)}
                      >
                        <FontAwesomeIcon icon={faEllipsisH} />
                      </button>
                      {topMenuOpen && (
                        <div className="support-dropdown support-dropdown--conversation">
                          <button type="button">
                            <FontAwesomeIcon icon={faCommentDots} /> Marcar como no leído
                          </button>
                          <button type="button">
                            <FontAwesomeIcon icon={faLink} /> Copiar enlace
                          </button>
                          <button type="button">
                            <FontAwesomeIcon icon={faEnvelope} /> Establecer Asunto
                          </button>
                          <button type="button">
                            <FontAwesomeIcon icon={faBook} /> Transcripción
                          </button>
                          <hr />
                          <button type="button">
                            <FontAwesomeIcon icon={faBan} /> Marcar como spam
                          </button>
                          <button type="button" className="danger">
                            <FontAwesomeIcon icon={faTrashAlt} /> Borrar conversación
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button color="primary" className="support-status-btn">
                    <FontAwesomeIcon icon={faArrowRight} /> Pendiente
                  </Button>
                </div>
                <div className="support-chat-thread">
                  {selectedConversation ? (
                    messagesLoading ? (
                      <div className="support-chat-thread__loading">
                        <span className="support-chat-thread__spinner" aria-hidden />
                        Cargando conversación...
                      </div>
                    ) : (
                    <>
                      <div className="support-language-pill">
                        {selectedConversation.flag}{" "}
                        {selectedConversation.session?.language || "Español"}
                      </div>
                      {messages.length > 0 && (
                        <div className="support-date-pill">
                          {messages[0].timestamp.date}
                        </div>
                      )}
                      {messages.map((message) => {
                        const isUser = message.senderType === "user";
                        return (
                          <div
                            key={message.id}
                            className={cn("support-message-row", {
                              "support-message-row--user": isUser,
                              "support-message-row--agent": !isUser,
                            })}
                          >
                            {isUser && (
                              <SupportAvatar
                                className="support-avatar support-avatar--sm"
                                color={selectedConversation.color}
                                name={selectedConversation.name}
                                avatarUrl={selectedConversation.avatarUrl}
                                countryCode={selectedConversation.countryCode}
                                country={selectedConversation.country}
                                showPresence={false}
                              />
                            )}
                            <div className="support-message-stack">
                              {!isUser && message.senderName && (
                                <small className="support-message-sender">
                                  {message.senderName}
                                </small>
                              )}
                              <span
                                className={cn("support-message", {
                                  "support-message--agent": !isUser,
                                })}
                              >
                                {message.content}
                              </span>
                              {message.timestamp?.time && (
                                <small className="support-message-time">
                                  {message.timestamp.time}
                                </small>
                              )}
                            </div>
                            {!isUser && (
                              <SupportAvatar
                                className="support-avatar support-avatar--sm"
                                color="var(--support-blue)"
                                name={message.senderName}
                                avatarUrl={message.senderAvatar}
                                showPresence={false}
                              />
                            )}
                          </div>
                        );
                      })}
                      <div ref={threadEndRef} aria-hidden />
                    </>
                    )
                  ) : (
                    <div className="support-chat-thread__empty">
                      Seleccioná una conversación del inbox.
                    </div>
                  )}
                </div>
                <div className="support-reply-panel">
                  {knowledgeOpen && (
                    <div className="support-knowledge-popover">
                      <div className="support-knowledge-popover__head">
                        <strong>Base de conocimiento</strong>
                        <span>🇪🇸 Español</span>
                        <small>↕ para navegar · ↵ para seleccionar · esc para salir</small>
                      </div>
                      <div className="support-knowledge-popover__body">
                        <div className="support-knowledge-tags">
                          {["Módulo Venta...", "Comenzar", "Comenzar", "Módulo Conv...", "Ventas", "Comenzar"].map(
                            (tag, idx) => (
                              <button key={`${tag}-${idx}`} type="button" className={cn({ selected: idx === 5 })}>
                                {tag}
                              </button>
                            )
                          )}
                        </div>
                        <div className="support-knowledge-articles">
                          {[
                            "Cómo crear una cuenta en Mercado Libre",
                            "Cómo cambiar una cuenta de Instagram a Empresa ?",
                            "Cómo activar notificaciones de Chat 💬 en la App 📱 ?",
                            "Admitir mensajería desde la APP de Instagram",
                            "Cómo agregar un nuevo producto en Woo Commerce",
                            "¿Cómo crear una página en Facebook para mi negocio?",
                          ].map((article, idx) => (
                            <button key={article} type="button" className={cn({ selected: idx === 5 })}>
                              {article}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="support-reply-tabs">
                    {["Responder", "Editar", "Nota", "Recordatorio", "Accesos directos"].map((tab) => (
                      <button key={tab} type="button">
                        {tab}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={cn({ active: knowledgeOpen })}
                      onClick={() => setKnowledgeOpen((open) => !open)}
                    >
                      Base de conocimiento
                    </button>
                    <button type="button">
                      Herram. IA <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  </div>
                  <div className="support-composer">
                    <button type="button" className={cn({ active: knowledgeOpen })}>
                      Base de cono...
                    </button>
                    <input
                      ref={replyInputRef}
                      placeholder={
                        selectedConversation
                          ? `Enviar su mensaje para ${selectedConversation.name} por chat...`
                          : "Seleccioná una conversación..."
                      }
                      value={replyDraft}
                      onChange={(e) => setReplyDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendAgentReply();
                        }
                      }}
                      disabled={!selectedConversation}
                    />
                    <div className="support-composer__emoji" ref={emojiPanelRef}>
                      {emojiOpen && (
                        <div className="support-composer__emoji-panel">
                          <Picker
                            native
                            color="#1f85e9"
                            sheetSize={32}
                            showPreview
                            showSkinTones
                            exclude={["recent"]}
                            onSelect={onEmojiSelect}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              width: "100%",
                              height: 260,
                              maxHeight: 260,
                            }}
                          />
                        </div>
                      )}
                      <button
                        ref={emojiToggleRef}
                        type="button"
                        className={cn("support-composer__icon-btn", {
                          active: emojiOpen,
                        })}
                        aria-label="Emojis"
                        title="Emojis"
                        disabled={!selectedConversation}
                        onClick={() => setEmojiOpen((open) => !open)}
                      >
                        <FontAwesomeIcon icon={faSmile} />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="support-composer__icon-btn"
                      aria-label="Adjuntar archivo"
                      title="Adjuntos (próximamente)"
                      disabled
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                    </button>
                    <button
                      type="button"
                      className="support-composer__icon-btn"
                      aria-label="Mensaje de voz"
                      title="Audio (próximamente)"
                      disabled
                    >
                      <FontAwesomeIcon icon={faMicrophone} />
                    </button>
                    <button
                      type="button"
                      className="support-send-btn"
                      onClick={sendAgentReply}
                      disabled={!replyDraft.trim() || !selectedConversation}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </div>
                </div>
              </main>

              <aside className="support-profile">
                {selectedConversation ? (
                  <>
                <div className="support-profile__head">
                  <SupportAvatar
                    className="support-avatar support-avatar--profile"
                    color={selectedConversation.color}
                    name={selectedConversation.name}
                    avatarUrl={selectedConversation.avatarUrl}
                    flag={selectedConversation.flag}
                    countryCode={selectedConversation.countryCode}
                    country={selectedConversation.country}
                    presence={getConversationPresence(selectedConversation, supportVisitors)}
                  />
                  <div>
                    <strong>{selectedConversation.name}</strong>
                    <a href={`mailto:${selectedConversation.email}`}>{selectedConversation.email}</a>
                    <small>
                      {formatLocationLabel(
                        selectedConversation.city,
                        selectedConversation.country,
                        selectedConversation.session
                      )}{" "}
                      · {selectedConversation.wembiiCompanyName}
                    </small>
                  </div>
                </div>
                <Button color="primary" block>
                  <FontAwesomeIcon icon={faUser} /> Ver perfil de {selectedConversation.name.split(" ")[0]}
                </Button>
                <div className="support-profile-section">
                  <h4>Enrutamiento de conversaciones</h4>
                  <p>
                    {selectedConversation.consultationLabel || "Ninguno asignado"}
                  </p>
                </div>
                <div className="support-profile-section support-profile-section--company">
                  <h4>Empresa asociada en Wembii</h4>
                  <p>
                    <FontAwesomeIcon icon={faBuilding} />{" "}
                    <strong>{selectedConversation.wembiiCompanyName}</strong>
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faUser} /> Contacto:{" "}
                    {selectedConversation.name}
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Información principal</h4>
                  <p>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                    {formatLocationLabel(
                      selectedConversation.city,
                      selectedConversation.country,
                      selectedConversation.session
                    )}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {formatLocalTime(selectedConversation.session?.timezone)} (
                    {selectedConversation.session?.timezone || "local"})
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faGlobeAmericas} /> {selectedConversation.flag}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faCommentDots} /> Chat
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Dispositivo del visitante</h4>
                  <p>
                    {selectedConversation.session?.browser || "Navegador desconocido"}{" "}
                    {selectedConversation.session?.os
                      ? `en ${selectedConversation.session.os}`
                      : ""}
                  </p>
                  <p>
                    {formatIpLabel(selectedConversation.session?.ipAddress)}
                    {selectedConversation.session?.country
                      ? ` · ${selectedConversation.session.country}`
                      : ""}
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Participantes de la conversación</h4>
                  {(selectedConversation.participants || [
                    { email: selectedConversation.email, name: selectedConversation.name },
                  ]).map((participant) => (
                    <p key={participant.email}>
                      <FontAwesomeIcon icon={faEnvelope} /> {participant.email}
                    </p>
                  ))}
                </div>
                  </>
                ) : null}
              </aside>
            </div>
          ) : (
            <div className="support-appearance">
              <section className="support-appearance__panel">
                <header className="support-appearance__head">
                  <h3>Configuración del chat visible para usuarios</h3>
                  <button
                    type="button"
                    onClick={() => resetAppearance()}
                  >
                    Restablecer
                  </button>
                </header>

                <div className="support-appearance__sections">
                  <section className="support-appearance-card">
                    <h4>Tema del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Color del encabezado</span>
                      <input
                        type="color"
                        value={appearance.headerColor}
                        onChange={(e) =>
                          setAppearanceField("headerColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de burbuja</span>
                      <input
                        type="color"
                        value={appearance.bubbleColor}
                        onChange={(e) =>
                          setAppearanceField("bubbleColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Fondo del widget</span>
                      <input
                        type="color"
                        value={appearance.widgetBg}
                        onChange={(e) =>
                          setAppearanceField("widgetBg", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de texto</span>
                      <input
                        type="color"
                        value={appearance.textColor}
                        onChange={(e) =>
                          setAppearanceField("textColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de acento</span>
                      <input
                        type="color"
                        value={appearance.accentColor}
                        onChange={(e) =>
                          setAppearanceField("accentColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Bordes widget</span>
                      <select
                        value={appearance.roundedStyle}
                        onChange={(e) =>
                          setAppearanceField(
                            "roundedStyle",
                            e.target.value as SupportChatAppearance["roundedStyle"]
                          )
                        }
                      >
                        <option value="soft">Suave</option>
                        <option value="rounded">Redondeado</option>
                        <option value="square">Cuadrado</option>
                      </select>
                    </div>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Textos del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Título del widget</span>
                      <input
                        value={appearance.widgetTitle}
                        onChange={(e) =>
                          setAppearanceField("widgetTitle", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Título de bienvenida</span>
                      <input
                        value={appearance.welcomeTitle}
                        onChange={(e) =>
                          setAppearanceField("welcomeTitle", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row support-appearance-row--textarea">
                      <span>Mensaje de bienvenida</span>
                      <textarea
                        rows={3}
                        value={appearance.welcomeMessage}
                        onChange={(e) =>
                          setAppearanceField("welcomeMessage", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Placeholder del input</span>
                      <input
                        value={appearance.inputPlaceholder}
                        onChange={(e) =>
                          setAppearanceField("inputPlaceholder", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>CTA principal</span>
                      <input
                        value={appearance.ctaLabel}
                        onChange={(e) =>
                          setAppearanceField("ctaLabel", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Estado online</span>
                      <input
                        value={appearance.onlineLabel}
                        onChange={(e) =>
                          setAppearanceField("onlineLabel", e.target.value)
                        }
                      />
                    </div>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Botón del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Posición del launcher</span>
                      <select
                        value={appearance.position}
                        onChange={(e) =>
                          setAppearanceField(
                            "position",
                            e.target.value as SupportChatAppearance["position"]
                          )
                        }
                      >
                        <option value="right">Derecha</option>
                        <option value="left">Izquierda</option>
                      </select>
                    </div>
                    <div className="support-appearance-row">
                      <span>Avatar principal</span>
                      <select
                        value={appearance.avatarType}
                        onChange={(e) =>
                          setAppearanceField(
                            "avatarType",
                            e.target.value as SupportChatAppearance["avatarType"]
                          )
                        }
                      >
                        <option value="logo">Logo Wembii</option>
                        <option value="agent">Agente</option>
                      </select>
                    </div>
                    {appearance.avatarType === "logo" ? (
                      <>
                        <div className="support-appearance-row">
                          <span>Logo personalizado</span>
                          <div className="support-appearance-avatar-upload">
                            {appearance.avatar ? (
                              <img
                                src={appearance.avatar}
                                alt=""
                                className="support-appearance-avatar-upload__preview"
                              />
                            ) : null}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadAvatar(file).catch(() => undefined);
                                e.target.value = "";
                              }}
                            />
                          </div>
                        </div>
                        <p className="support-appearance-card__hint">
                          Con &quot;Logo Wembii&quot; se muestra el logo de Wembii en el
                          chat. Podés subir un logo de marca para reemplazarlo.
                        </p>
                      </>
                    ) : (
                      <p className="support-appearance-card__hint">
                        En modo Agente, el encabezado muestra hasta 4 admins en línea con
                        su foto de perfil. Si nadie está online, se muestra el equipo demo
                        con el logo de Wembii. El último en responder se destaca a la derecha.
                      </p>
                    )}
                    <label className="support-appearance-switch">
                      <span>Mostrar nombre de agente</span>
                      <input
                        type="checkbox"
                        checked={appearance.showAgentName}
                        onChange={(e) =>
                          setAppearanceField("showAgentName", e.target.checked)
                        }
                      />
                    </label>
                    <label className="support-appearance-switch">
                      <span>Mostrar timestamp en mensajes</span>
                      <input
                        type="checkbox"
                        checked={appearance.showTimestamp}
                        onChange={(e) =>
                          setAppearanceField("showTimestamp", e.target.checked)
                        }
                      />
                    </label>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Botones rápidos del chat</h4>
                    <p className="support-appearance-card__hint">
                      Define si el chat abre con consulta comercial, técnica, ambas o ninguna.
                    </p>
                    <label className="support-appearance-switch">
                      <span>Mostrar clasificación inicial</span>
                      <input
                        type="checkbox"
                        checked={appearance.showQuickButtons}
                        onChange={(e) =>
                          setAppearanceField("showQuickButtons", e.target.checked)
                        }
                      />
                    </label>
                    <label className="support-appearance-switch">
                      <span>Mostrar botón comercial</span>
                      <input
                        type="checkbox"
                        checked={appearance.showCommercialQuickButton}
                        onChange={(e) =>
                          setAppearanceField(
                            "showCommercialQuickButton",
                            e.target.checked
                          )
                        }
                        disabled={!appearance.showQuickButtons}
                      />
                    </label>
                    <div className="support-appearance-row">
                      <span>Texto botón comercial</span>
                      <input
                        value={appearance.quickButtonCommercial}
                        onChange={(e) =>
                          setAppearanceField("quickButtonCommercial", e.target.value)
                        }
                        disabled={
                          !appearance.showQuickButtons ||
                          !appearance.showCommercialQuickButton
                        }
                      />
                    </div>
                    <label className="support-appearance-switch">
                      <span>Mostrar botón técnico</span>
                      <input
                        type="checkbox"
                        checked={appearance.showTechnicalQuickButton}
                        onChange={(e) =>
                          setAppearanceField(
                            "showTechnicalQuickButton",
                            e.target.checked
                          )
                        }
                        disabled={!appearance.showQuickButtons}
                      />
                    </label>
                    <div className="support-appearance-row">
                      <span>Texto botón técnico</span>
                      <input
                        value={appearance.quickButtonTechnical}
                        onChange={(e) =>
                          setAppearanceField("quickButtonTechnical", e.target.value)
                        }
                        disabled={
                          !appearance.showQuickButtons ||
                          !appearance.showTechnicalQuickButton
                        }
                      />
                    </div>
                  </section>
                </div>
              </section>

              <section className="support-appearance__preview-wrap">
                <header>
                  <h4>Preview en vivo</h4>
                  <span>Así verá el chat un usuario final de Wembii</span>
                </header>
                <SupportChatLivePreview appearance={appearance} />
              </section>
            </div>
          )}
        </div>
      </Content>
    </Layout>
  );
}
